import { faTableColumns, faTableRows } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  CLOSE_REASON,
  type CloseHandler,
  type CloseReason,
  IGNORED_REASONS
} from '@/pages/Administration/Devices/Devices.types';
import useStyles from '@/pages/Administration/Devices/SyncDevices/SyncDevicesDialog/SyncDevicesDialog.style';

import { diffViewModeAtom } from '../../Devices.store';
import { useSyncDevicesDialog } from '../SyncDevices.hooks';
import {
  type DeviceSyncDataModel,
  type GroupedDevicesSyncData,
  groupeddevicesSyncDataAtom,
  groupedSyncItemsAtom
} from '../SyncDevices.types';
import SyncDevicesForm from '../SyncDevicesForm';
import useCustomNotification, {
  type CustomNotificationProps
} from '../useCustomNotification';

const SyncDevicesDialog = () => {
  const { classes } = useStyles();
  const formMethods = useForm();
  const { reset } = formMethods;
  const {
    open: showDevicePopup,
    mode,
    mutation,
    openSyncDevicesDialog,
    closeSyncDevicesDialog
  } = useSyncDevicesDialog();

  const [, setGroupedDeviceSyncData] = useAtom(groupeddevicesSyncDataAtom);
  const [groupedSyncData, setGroupedSyncData] = useAtom(groupedSyncItemsAtom);
  const [scroll] = useState<DialogProps['scroll']>('paper');
  const [diffViewMode, setDiffViewMode] = useAtom(diffViewModeAtom);

  const confirmMessageBeforeSync = 'Do you want to initiate the sync process?';
  const syncValidationMessage = 'Please select at least one device to sync';
  const [customNotifyProps, setCustomNotifyProps] =
    useState<CustomNotificationProps>({
      title: '',
      message: '',
      severity: 'info',
      buttonType: 'Close'
    });

  const {
    ConfirmationModal: CustomNotificationModal,
    confirm: ShowCustomNotification
  } = useCustomNotification(customNotifyProps);

  const clearDeviceSyncSelecttion = () => {
    const updatedGrpSyncData = groupedSyncData.map(
      (row: GroupedDevicesSyncData) => {
        // Reset all items' isSelected to false
        const updatedItems =
          row.items?.map((item) => ({
            ...item,
            isSelected: false
          })) ?? [];
        return {
          ...row,
          items: updatedItems,
          selectedCount: 0
        };
      }
    );

    setGroupedDeviceSyncData(updatedGrpSyncData);
  };

  const resetAndClose = () => {
    clearDeviceSyncSelecttion();
    closeSyncDevicesDialog();
    setGroupedDeviceSyncData([]);
    setGroupedSyncData([]);
    // reset form values
    reset();
  };
  const changeViewMode = (_event: React.SyntheticEvent, viewMode: string) => {
    setDiffViewMode(viewMode);
  };

  const control = {
    value: diffViewMode,
    onChange: changeViewMode,
    exclusive: true
  };

  const getSelectedDevices = () => {
    const selectedDevices: string[] = [];

    groupedSyncData.forEach((row: GroupedDevicesSyncData) => {
      const updatedDevices = row?.items?.filter(
        (device: DeviceSyncDataModel) => device.isSelected
      );
      if (updatedDevices) {
        selectedDevices.push(
          ...updatedDevices.map(
            (device: DeviceSyncDataModel) => device.hostName || ''
          )
        );
      }
    });
    return selectedDevices;
  };

  const handleClose: CloseHandler = (_event: object, reason: CloseReason) => {
    if (IGNORED_REASONS.includes(reason)) {
      return;
    }
    resetAndClose();
  };

  const ApplySync = () => {
    const devicesList = getSelectedDevices();
    if (devicesList.length > 0) {
      openSyncDevicesDialog('confirmSyncDevices');
    } else {
      setCustomNotifyProps({
        title: 'Sync Devices',
        message: syncValidationMessage,
        severity: 'info',
        buttonType: 'Close'
      });
      ShowCustomNotification();
    }
  };

  const RedirectToNewSync = () => {
    openSyncDevicesDialog('newSyncDevices');
  };

  const handleSyncDevicesSubmit = async () => {
    const payload = getSelectedDevices();
    if (payload.length > 0) {
      setCustomNotifyProps({
        title: 'Sync Devices',
        message: confirmMessageBeforeSync,
        severity: 'warning',
        buttonType: 'YesNo'
      });
      const confirmed = await ShowCustomNotification();
      if (confirmed && payload.length > 0) {
        mutation.mutate(payload);
        resetAndClose();
      }
    } else {
      setCustomNotifyProps({
        title: 'Sync Devices',
        message: syncValidationMessage,
        severity: 'info',
        buttonType: 'Close'
      });
      ShowCustomNotification();
    }
  };

  return (
    <Dialog
      open={showDevicePopup}
      onClose={handleClose}
      scroll={scroll}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle id="scroll-dialog-title">
        {mode === 'newSyncDevices' && <>Sync Devices</>}
        {mode === 'confirmSyncDevices' && <>Review Sync Devices</>}
        {mode === 'viewSyncInProgress' && <>View Sync Details</>}
      </DialogTitle>
      <DialogContent>
        {mode === 'confirmSyncDevices' && (
          <Box className={classes.syncInfo}>
            <Alert severity="info">
              <Typography variant="h6">
                Please review your changes below that you are planning to sync.
              </Typography>
            </Alert>
          </Box>
        )}
        <Box className={classes.fixedToggleHeader}>
          <ToggleButtonGroup
            size="small"
            {...control}
            className={classes.toggleButtons}
            aria-label="Small sizes"
          >
            <ToggleButton value="unified" key="row">
              <FontAwesomeIcon icon={faTableRows} size="lg" />
            </ToggleButton>
            ,
            <ToggleButton value="split" key="columns">
              <FontAwesomeIcon icon={faTableColumns} size="lg" />
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ textAlign: 'right', py: 2, pr: 2 }}>Apply Changes</Box>
        </Box>
        <FormProvider {...formMethods}>
          <form>
            <SyncDevicesForm />
          </form>
        </FormProvider>
      </DialogContent>
      <Box className={classes.legend}>
        <Box className={classes.diffLegent}>
          <Box className="diff-removed" />
          Production State
        </Box>
        <Box className={classes.diffLegent}>
          <Box className="diff-added" sx={{ marginLeft: '.5rem' }} />
          Planned Changes
        </Box>
      </Box>
      <DialogActions>
        <Box>
          {mode === 'newSyncDevices' && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              type="button"
              sx={{ marginLeft: '.5rem' }}
              onClick={(e: React.MouseEvent<HTMLElement>) =>
                handleClose(e, CLOSE_REASON.BUTTON)
              }
            >
              Cancel
            </Button>
          )}

          {mode === 'confirmSyncDevices' && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              type="button"
              sx={{ marginLeft: '.5rem' }}
              onClick={RedirectToNewSync}
            >
              Back
            </Button>
          )}

          {mode === 'newSyncDevices' && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              type="button"
              sx={{ marginLeft: '.5rem' }}
              onClick={ApplySync}
            >
              Review & Sync
            </Button>
          )}

          {mode === 'confirmSyncDevices' && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              type="button"
              sx={{ marginLeft: '.5rem' }}
              onClick={handleSyncDevicesSubmit}
            >
              {!mutation.isPending && <span>Sync</span>}
              {mutation.isPending && (
                <CircularProgress color="secondary" size={20} />
              )}
            </Button>
          )}

          {mode === 'viewSyncInProgress' && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              type="button"
              sx={{ marginLeft: '.5rem' }}
              onClick={(e: React.MouseEvent<HTMLElement>) =>
                handleClose(e, CLOSE_REASON.BUTTON)
              }
            >
              Close
            </Button>
          )}
        </Box>
      </DialogActions>
      <CustomNotificationModal />
    </Dialog>
  );
};
export default SyncDevicesDialog;
